import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CircleNotch } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth, AuthCallback exchanges the session_id first — skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch (e) { console.error("Logout request failed", e); }
    setUser(null);
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/library";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, setUser, checkAuth, logout, loginWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

function FullScreenSpinner({ label = "Loading…" }) {
  return (
    <div className="h-screen flex items-center justify-center">
      <div className="flex items-center gap-3 text-gray-500 font-mono">
        <CircleNotch size={22} className="animate-spin" /> {label}
      </div>
    </div>
  );
}

// Handles the post-Google-auth landing (URL has #session_id=...). Exchanges it for a
// session cookie, then lands the user on their library.
export function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    (async () => {
      if (!sessionId) { navigate("/", { replace: true }); return; }
      try {
        const u = await api.authSession(sessionId);
        setUser(u);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/library", { replace: true });
      } catch {
        navigate("/?auth_error=1", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <FullScreenSpinner label="Signing you in…" />;
}

export function ProtectedRoute({ children, adminOnly = false }) {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/", { replace: true });
    else if (adminOnly && user.role !== "admin") navigate("/library", { replace: true });
  }, [user, loading, adminOnly, navigate]);

  if (loading) return <FullScreenSpinner />;
  if (!user) return null;
  if (adminOnly && user.role !== "admin") return null;
  return children;
}
